import React from 'react';

import PropTypes from 'prop-types';

import { noop } from '@adsk/alloy-react-helpers';
import { useTheme } from '@adsk/alloy-react-styles';
import { stylePropType } from '@adsk/alloy-react-theme';

import Calendar from './Calendar';
import DefaultFooter from './DefaultFooter';
import DefaultFooterActions from './DefaultFooterActions';
import { getSelectedDates, getToday, isSelectable } from '../helpers';
import useCalendars from '../hooks/useCalendars';
import useDatePicker from '../hooks/useDatePicker';
import useDateSelection from '../hooks/useDateSelection';
import useNavigation from '../hooks/useNavigation';
import useRangeHover from '../hooks/useRangeHover';
import * as types from '../types';

const DATE_PICKER_CALENDAR_WIDTH = 312;

type OverlayProps = Pick<ReturnType<typeof useDatePicker>, 'toggleOverlay'> &
  Pick<
    ReturnType<typeof useDateSelection>,
    'onDateSelected' | 'resetSelection'
  > &
  Pick<
    types.DatePickerAllArgs,
    | 'startDate'
    | 'endDate'
    | 'value'
    | 'minDate'
    | 'maxDate'
    | 'double'
    | 'timeZone'
    | 'onMonthChange'
    | 'firstDayOfWeek'
    | 'showOutsideDays'
    | 'isDateSelectable'
    | 'yearsToDisplay'
    | 'calendarProps'
    | 'hasEventForDate'
    | 'tooltipContentForDate'
    | 'renderFooter'
    | 'renderFooterActions'
  > &
  types.OverlayProps & { range?: boolean; shouldShowSelection: boolean };

const Overlay = React.forwardRef<HTMLDivElement, OverlayProps>(
  (
    {
      value: dateProp,
      double,
      calendarProps,
      range,
      startDate,
      endDate,
      minDate,
      maxDate,
      timeZone,
      resetSelection,
      onDateSelected,
      onMonthChange = noop,
      firstDayOfWeek,
      showOutsideDays,
      isDateSelectable,
      yearsToDisplay = 20,
      toggleOverlay,
      hasEventForDate,
      tooltipContentForDate,
      renderFooter = (p) => <DefaultFooter {...p} />,
      renderFooterActions = (p) => <DefaultFooterActions {...p} />,
      className,
      style,
      shouldShowSelection,
      ...rest
    },
    ref,
  ) => {
    const theme = useTheme();
    const t = theme.tokens?.component?.datePicker;
    const useWeaveWeekGrid = !!t?.calendar?.weeks;
    const resolvedShowOutsideDays = useWeaveWeekGrid
      ? (showOutsideDays ?? true)
      : (showOutsideDays ?? false);

    const [stateHoveredDate, onDateHover] = useRangeHover({
      range,
      startDate,
      endDate,
      minDate,
      maxDate,
    });

    const {
      activeMonths,
      updateActiveMonths,
      setMonth,
      setYear,
      nextMonth,
      prevMonth,
      resetViewToTodayAndSelect,
    } = useNavigation({
      range,
      value: dateProp,
      startDate,
      endDate,
      numberOfMonths: double ? 2 : 1,
      onDateSelected,
      onMonthChange,
      minDate,
      maxDate,
      timeZone,
    });
    const selectedDates = getSelectedDates({
      range,
      date: dateProp,
      startDate,
      endDate,
    });
    const calendars = useCalendars({
      activeMonths,
      selectedDates,
      minDate,
      maxDate,
      firstDayOfWeek,
      showOutsideDays: resolvedShowOutsideDays,
      isDateSelectable,
      yearsToDisplay,
      timeZone,
    });

    const totalCalendars = calendars.length;
    const overlayWidth =
      totalCalendars > 1 && t?.doubleCalendarWidth
        ? t.doubleCalendarWidth
        : (t?.calendarWidth ?? DATE_PICKER_CALENDAR_WIDTH) * totalCalendars;

    // Check if today's date is within the selectable range
    const today = getToday(timeZone);
    const isTodaySelectable = isSelectable({ minDate, maxDate, date: today });

    return (
      <div
        ref={ref}
        className={className}
        css={[
          {
            minHeight: 321,
            display: 'flex',
            flexDirection: 'column',
            backgroundColor: t?.overlay?._?.backgroundColor,
            boxShadow: t?.overlay?._?.boxShadow,
            borderRadius: t?.overlay?._?.borderRadius,
            paddingTop: t?.overlay?._?.paddingTop,
            paddingBottom: t?.overlay?._?.paddingBottom,
            width: overlayWidth,
            minWidth: overlayWidth,
            maxWidth: overlayWidth,
          },
          style,
        ]}
        {...rest}
      >
        <div
          css={{
            display: 'flex',
            columnGap: t?.calendar?._?.columnGap,
          }}
        >
          {calendars.map((calendar, key) => {
            return (
              <Calendar
                key={key}
                range={range}
                calendar={calendar}
                calendars={calendars}
                stateHoveredDate={stateHoveredDate}
                onDateHover={onDateHover}
                setMonth={setMonth}
                setYear={setYear}
                nextMonth={nextMonth}
                prevMonth={prevMonth}
                onDateSelected={onDateSelected}
                updateActiveMonths={updateActiveMonths}
                hasEventForDate={hasEventForDate}
                tooltipContentForDate={tooltipContentForDate}
                startDate={startDate}
                endDate={endDate}
                minDate={minDate}
                maxDate={maxDate}
                shouldShowSelection={shouldShowSelection}
                {...calendarProps}
              />
            );
          })}
        </div>
        {renderFooter?.({
          today: resetViewToTodayAndSelect,
          reset: resetSelection,
          toggleOverlay,
          renderFooterActions,
          isTodaySelectable,
        })}
      </div>
    );
  },
);

Overlay.displayName = 'Overlay';

Overlay.propTypes = {
  /** Styles applied to the root element */
  style: stylePropType,
  /** Class applied to the root element */
  className: PropTypes.string,
  /** Whether the we should display double date picker */
  double: PropTypes.bool,
  onDateSelected: PropTypes.func.isRequired,
  resetSelection: PropTypes.func.isRequired,
  toggleOverlay: PropTypes.func.isRequired,
  /** A selected date for a calendar without range */
  value: PropTypes.instanceOf(Date),
  /** A selected start date for a calendar with range data (should be used with range=true) */
  startDate: PropTypes.instanceOf(Date),
  /** A selected end date for a calendar with range data (should be used with range=true) */
  endDate: PropTypes.instanceOf(Date),
  /** Allow selection of dates after minDate, overrides `disablePastDates` */
  minDate: PropTypes.instanceOf(Date),
  /** Allow selection of dates before maxDate, overrides `disableFutureDates` */
  maxDate: PropTypes.instanceOf(Date),
  /** Flag whether the date picker is for a date range or a single value */
  range: PropTypes.bool,
  timeZone: PropTypes.string,
  /** A callback function called when the selected date(s) has been changed */
  onChange: PropTypes.func,
  /** A callback function called when the active months have changed (arrow click, month/year selection) */
  onMonthChange: PropTypes.func,
  /** Handler to manually check if date is selectable */
  isDateSelectable: PropTypes.func,
  /** First day of week to display */
  firstDayOfWeek: PropTypes.oneOf([0, 1, 2, 3, 4, 5, 6]),
  /** Fill blanks with days from prev/next month. Defaults to true in Weave week-grid mode, false in Alloy. */
  showOutsideDays: PropTypes.bool,
  /* Props passed to each calendar */
  calendarProps: PropTypes.object,
  /** Render custom footer, executed in DatePickerOverlay */
  renderFooter: PropTypes.func,
  /** Render custom footer actions, executed in DatePickerOverlay */
  renderFooterActions: PropTypes.func,
  /** Determines if Day Cell should render an event dot */
  hasEventForDate: PropTypes.func,
  /** Tooltip content for Day Cell */
  tooltipContentForDate: PropTypes.func,
  /** How much years to display under and above current year, defaults to +-20 */
  yearsToDisplay: PropTypes.number,
};

export default Overlay;
